import type { InvoicePlant, InvoiceRow, Principal } from '@dru/types';

/**
 * What a company-side billing screen may offer, decided from the signed-in
 * principal and the plants the server returned (docs/37 Step 5).
 *
 * Convenience only. The server filters every invoice read by the caller's
 * plant binding; this decides which choices to draw, never which rows exist.
 */
export type PlantChoice =
  /** Company-wide: "All plants" plus each plant. */
  | { mode: 'company'; options: InvoicePlant[] }
  /** Bound to one plant: shown as a label, no selector. */
  | { mode: 'single'; plant: InvoicePlant }
  /** Bound to several plants: switch among exactly those, no "All plants". */
  | { mode: 'multi'; options: InvoicePlant[] }
  /** Bound to nothing readable — no plants, or bindings that disagree. */
  | { mode: 'none' };

/** Whether this principal is narrowed to plants rather than company-wide. */
export function isPlantBound(principal: Pick<Principal, 'scopes'> | null | undefined): boolean {
  const scopes = principal?.scopes ?? [];
  const wide = scopes.some((s) => s.scopeType === 'TENANT' || s.scopeType === 'GLOBAL');
  return !wide && scopes.some((s) => s.scopeType === 'PLANT');
}

export function plantChoice(
  principal: Pick<Principal, 'scopes'> | null | undefined,
  plants: InvoicePlant[],
): PlantChoice {
  if (plants.length === 0) return { mode: 'none' };
  if (!isPlantBound(principal)) return { mode: 'company', options: plants };
  if (plants.length === 1) return { mode: 'single', plant: plants[0]! };
  return { mode: 'multi', options: plants };
}

/**
 * The plant a screen shows, given what was asked for.
 *
 * A plant outside the choice falls back rather than being passed through: a
 * bookmarked Plant B link opened by a Plant A login shows Plant A, and the
 * server would have returned nothing for B anyway.
 */
export function selectedPlant(choice: PlantChoice, requested: string | null): string {
  switch (choice.mode) {
    case 'none': return '';
    case 'single': return choice.plant.id;
    case 'company': return choice.options.some((p) => p.id === requested) ? requested! : '';
    case 'multi': return choice.options.some((p) => p.id === requested) ? requested! : choice.options[0]!.id;
  }
}

export type Standing = 'IN_PREPARATION' | 'UNPAID' | 'PARTIAL' | 'PAID' | 'VOID';

/** Where an invoice stands for the company paying it. */
export function standingOf(i: Pick<InvoiceRow, 'status' | 'paidPaise' | 'outstandingPaise'>): Standing {
  if (i.status === 'DRAFT') return 'IN_PREPARATION';
  if (i.status === 'VOID') return 'VOID';
  if ((i.outstandingPaise ?? 0) <= 0) return 'PAID';
  return (i.paidPaise ?? 0) > 0 ? 'PARTIAL' : 'UNPAID';
}

export const STANDING_LABEL: Record<Standing, string> = {
  IN_PREPARATION: 'In preparation', UNPAID: 'Unpaid', PARTIAL: 'Partly paid', PAID: 'Paid', VOID: 'Void',
};

const MONTH = new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const DAY = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * A billing period the way an accounts team says it: "Aug 2026" for a whole
 * calendar month, "1 Aug – 15 Aug 2026" otherwise. Periods are dates without
 * a time, so they are read in UTC — a local-time reading moves them a day.
 */
export function periodLabel(start: string, end: string): string {
  const s = new Date(start.slice(0, 10) + 'T00:00:00Z');
  const e = new Date(end.slice(0, 10) + 'T00:00:00Z');
  const lastDay = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + 1, 0)).getUTCDate();
  if (s.getUTCDate() === 1 && s.getUTCFullYear() === e.getUTCFullYear()
      && s.getUTCMonth() === e.getUTCMonth() && e.getUTCDate() === lastDay) {
    return MONTH.format(s);
  }
  return `${DAY.format(s)} – ${DAY.format(e)} ${e.getUTCFullYear()}`;
}
